// Block library screen — read-only viewer of registered Blocks. Per
// spec/build-plan.md P-024 row + tech-arch.md §6.
//
// **For each registered Block**: id, label, category, schema summary.
// Categories: header / content / footer / meta. Blocks are grouped by
// category; within a category, ordered by registry insertion order
// (`Object.values(blocks)` preserves declaration order in JS objects).
//
// **Schema summary = flat field-list.** For each Block's `optionsSchema` +
// `inputsSchema`: if the schema is a ZodObject, list its top-level field
// names with a type label; if it's a ZodUnion (or other non-object), label
// it generically. The full Zod-tree visualization (recursive structure +
// constraints + descriptions) is form-generator territory (P-025+) and
// would duplicate logic that lives in the form generator. P-035 polish
// can revisit if richer summaries prove useful.
//
// **Module-scope precomputation.** The Block registry is immutable, so
// `ENTRIES` + `GROUPED` derive once at module load — never per render.
// `<LibraryScreen />` is a pure read of those constants.
//
// **No mutation; pure read-only view.** Library doesn't write to state and
// doesn't navigate. Read-only consumer of the registry.

import type { z } from 'zod'

import type { BlockCategory } from '../../types'
import { blocks } from '../../registry/blocks'
import { zodDef } from '../form/zod-introspect'

interface BlockEntry {
  readonly id: string
  readonly label: string
  readonly category: BlockCategory
  readonly options: SchemaSummary
  readonly inputs: SchemaSummary
}

interface SchemaSummary {
  readonly kind: 'object' | 'union' | 'other'
  readonly fields: readonly FieldSummary[]
  readonly note?: string
}

interface FieldSummary {
  readonly name: string
  readonly type: string
  readonly optional: boolean
}

function describeField(name: string, schema: z.ZodTypeAny): FieldSummary {
  const def = zodDef(schema)
  if (
    def.typeName === 'ZodOptional' ||
    def.typeName === 'ZodDefault' ||
    def.typeName === 'ZodNullable'
  ) {
    return { name, type: zodTypeLabel(def.innerType), optional: true }
  }
  return { name, type: zodTypeLabel(schema), optional: false }
}

/** Best-effort type label for a Zod schema. Exported for direct testing. */
export function zodTypeLabel(schema: z.ZodTypeAny | { _def?: { typeName?: string } }): string {
  const def = zodDef(schema as z.ZodTypeAny)
  const t: string = def?.typeName ?? 'unknown'
  switch (t) {
    case 'ZodString':
      return 'string'
    case 'ZodNumber':
      return 'number'
    case 'ZodBoolean':
      return 'boolean'
    case 'ZodEnum':
      return `enum(${(def.values as readonly string[]).join(' | ')})`
    case 'ZodLiteral':
      return `literal(${JSON.stringify(def.value)})`
    case 'ZodArray':
      return `array<${zodTypeLabel(def.type)}>`
    case 'ZodObject':
      return 'object'
    case 'ZodUnion':
      return 'union'
    case 'ZodUndefined':
      return 'undefined'
    case 'ZodNull':
      return 'null'
    default:
      return t.replace(/^Zod/, '').toLowerCase()
  }
}

/** Summarize a top-level Block schema for the library viewer. */
export function summarizeSchema(schema: z.ZodTypeAny): SchemaSummary {
  const def = zodDef(schema)
  if (def.typeName === 'ZodObject') {
    const shape = def.shape() as Record<string, z.ZodTypeAny>
    const fields = Object.entries(shape).map(([name, s]) => describeField(name, s))
    return { kind: 'object', fields }
  }
  if (def.typeName === 'ZodUnion') {
    const variants = def.options as readonly z.ZodTypeAny[]
    return {
      kind: 'union',
      fields: [],
      note: `Discriminated union of ${variants.length} variants`,
    }
  }
  return { kind: 'other', fields: [], note: zodTypeLabel(schema) }
}

const CATEGORY_ORDER: readonly BlockCategory[] = ['header', 'content', 'footer', 'meta']

function categoryLabel(c: BlockCategory): string {
  return c[0]!.toUpperCase() + c.slice(1)
}

interface CategoryGroup {
  readonly category: BlockCategory
  readonly items: readonly BlockEntry[]
}

const ENTRIES: readonly BlockEntry[] = Object.values(blocks).map((b) => ({
  id: b.id,
  label: b.label,
  category: b.category,
  options: summarizeSchema(b.optionsSchema),
  inputs: summarizeSchema(b.inputsSchema),
}))

const GROUPED: readonly CategoryGroup[] = CATEGORY_ORDER.map((category) => ({
  category,
  items: ENTRIES.filter((e) => e.category === category),
}))

function SchemaSection({ title, summary }: { title: string; summary: SchemaSummary }) {
  return (
    <div>
      <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</div>
      {summary.kind === 'object' ? (
        <ul class="m-0 mt-1 list-none space-y-0.5 p-0">
          {summary.fields.map((f) => (
            <li key={f.name} class="text-sm text-gray-800">
              <code class="font-mono text-gray-900">{f.name}</code>
              {f.optional && <span class="text-gray-500">?</span>}
              <span class="text-gray-500">: {f.type}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p class="m-0 mt-1 text-sm italic text-gray-500">{summary.note}</p>
      )}
    </div>
  )
}

export function LibraryScreen() {
  return (
    <div class="space-y-8">
      <header>
        <h2 class="m-0 text-lg font-semibold">Block library</h2>
        <p class="m-0 mt-2 text-sm text-gray-600">
          Read-only reference for the {ENTRIES.length} registered Blocks. The
          form generator at P-025+ will read these schemas to produce
          per-Block forms.
        </p>
      </header>

      {GROUPED.map(({ category, items }) => (
        <section key={category} aria-labelledby={`library-cat-${category}`}>
          <h3
            id={`library-cat-${category}`}
            class="m-0 mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600"
          >
            {categoryLabel(category)}
          </h3>
          <ul class="m-0 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-2">
            {items.map((entry) => (
              <li key={entry.id} class="rounded border border-gray-200 p-4">
                <div class="flex items-baseline justify-between gap-2">
                  <h4 class="m-0 text-base font-semibold text-gray-900">{entry.label}</h4>
                  <code class="text-xs text-gray-500">{entry.id}</code>
                </div>
                <div class="mt-3 space-y-3">
                  <SchemaSection title="Options" summary={entry.options} />
                  <SchemaSection title="Inputs" summary={entry.inputs} />
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
